import z from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import FormSection from './components/FormSection';
import FormFieldLabel from './components/FormFieldLabel';
import FormTextField from './components/FormTextField';
import { useDispatch, useSelector } from 'react-redux';
import { ProjectQualityControlFormData } from '../../api-client/autogenerated';
import FormDatePicker from './components/FormDatePicker';
import { addSnackbar } from '../../features/snackbar/actions';
import {
  editProjectQualityControlFormById,
  insertProjectQualityControlForm,
} from '../../models/api/project-lifecycle';
import { getProjectState, getProjectUsersState } from '../../features/project/selectors';
import Button from '@material-ui/core/Button';
import CircularLoader from '../loader/CircularLoader';
import React, { useEffect, useMemo } from 'react';
import _ from 'lodash';
import { allowNavigation, blockNavigation } from '../../features/navigation/actions';
import { reloadProject } from '../../features/project/actions';
import { useMediaQuery } from '@material-ui/core';
import dayjs from 'dayjs';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { Save } from '@material-ui/icons';
import FormSelect from './components/FormSelect';
import FormUserSelect from './components/FormUserSelect';
import { ProjectFilePathLink } from './types';
import { formatProjectLocation } from '../../scripts/utils';
import { WASTE_ELIMINATION_OPTIONS } from './data';
import ProjectQualityControlPhaseRow, {
  QUALITY_CONTROL_PHASE_COLUMNS,
  QUALITY_CONTROL_PHASE_GRID_COLUMNS,
  QUALITY_CONTROL_PHASE_ROWS,
} from './components/ProjectQualityControlPhaseRow';

const useStyles = makeStyles(() =>
  createStyles({
    hideOnPrint: {
      '@media print': {
        display: 'none',
      },
    },
    printShort: {
      '@media print': {
        width: '60%',
      },
    },
    // Sits between the QC Date row and the first section, so its margin is one of the larger gaps
    // on a printed copy.
    subtitle: {
      marginBottom: 24,
      '@media print': {
        marginBottom: 6,
      },
    },
    // A page break before Design Team Report earns its keep on screen-sized paper, but the
    // padding above it is dead space once everything else has tightened.
    printBreakBefore: {
      pageBreakBefore: 'always',
      '@media print': {
        paddingTop: 8,
      },
    },
    // Fills the grid cell so the dropdown stands as tall as the free-text
    // fields either side of it, with long selections wrapping rather than
    // being cut off at one line. The padding matches the multiline text
    // fields (18.5px/14px) so all three columns start their text on the
    // same line; the wider right padding keeps clear of the arrow.
    fillCell: {
      flexGrow: 1,
      alignItems: 'stretch',
      '& .MuiSelect-select': {
        display: 'flex',
        alignItems: 'flex-start',
        height: 'auto',
        whiteSpace: 'normal',
        padding: '18.5px 32px 18.5px 14px',
      },
    },
  }),
);

const schema = z.object({
  qcDate: z.string(),

  projectNumber: z.string().optional(),
  projectName: z.string().optional(),
  projectAddress: z.string().optional(),
  jurisdiction: z.string().optional(),
  projectDescription: z.string().optional(),
  architectOfRecord: z.string().optional(),
  projectManager: z.string().optional(),
  // Multi-select; see readStoredForm for the single string a form saved while these were
  // free-text fields holds.
  designTeam: z.array(z.string()).optional(),
  consultantFirms: z.array(z.string()).optional(),
  qcPeerReviewer1: z.string().optional(),
  qcPeerReviewer2: z.string().optional(),

  phaseDueDateSd: z.string().optional(),
  phaseDueDateDd: z.string().optional(),
  phaseDueDateCd: z.string().optional(),
  phaseDueDateSsDocs: z.string().optional(),

  designReviewSd: z.string().optional(),
  designReviewDd: z.string().optional(),
  designReviewCd: z.string().optional(),
  designReviewSsDocs: z.string().optional(),

  buildingCodeReviewSd: z.string().optional(),
  buildingCodeReviewDd: z.string().optional(),
  buildingCodeReviewCd: z.string().optional(),
  buildingCodeReviewSsDocs: z.string().optional(),

  qaReviewsSd: z.string().optional(),
  qaReviewsDd: z.string().optional(),
  qaReviewsCd: z.string().optional(),
  qaReviewsSsDocs: z.string().optional(),

  qcPeerReviewSd: z.string().optional(),
  qcPeerReviewDd: z.string().optional(),
  qcPeerReviewCd: z.string().optional(),
  qcPeerReviewSsDocs: z.string().optional(),

  currentPhase: z.string().optional(),
  areasUnderDevelopment: z.string().optional(),

  clientValuesAndGoals: z.string().optional(),
  clientMeasuresForSuccess: z.array(z.string()).optional(),
  clientMeasuresForSuccessOther: z.string().optional(),
  examineOurProcesses: z.string().optional(),

  reviewedBy: z.string().optional(),
  innovativeProcesses: z.string().optional(),
  innovationDetails: z.string().optional(),
  innovativeProducts: z.string().optional(),

  qaRevitPrompts: z.string().optional(),

  projectFolderLink: z.string().optional(),
  accLink: z.string().optional(),
  centerlineLink: z.string().optional(),

  // What each Project File Paths field links to, keyed by that field's name. A field with no
  // entry is plain typed text. See ProjectFilePathField.
  projectFilePathLinks: z
    .record(
      z.string(),
      z.object({
        documentId: z.string().optional(),
        documentType: z.string().optional(),
        fileNodeKey: z.string().optional(),
        fileNodeOwnerId: z.string().optional(),
      }),
    )
    .optional(),
});

type FormValues = z.infer<typeof schema>;

const getDefaultValues = (): FormValues => {
  return {
    qcDate: dayjs().format('MM/DD/YYYY'),

    projectNumber: '',
    projectName: '',
    projectAddress: '',
    jurisdiction: '',
    projectDescription: '',
    architectOfRecord: '',
    projectManager: '',
    designTeam: [],
    consultantFirms: [],
    qcPeerReviewer1: '',
    qcPeerReviewer2: '',

    phaseDueDateSd: '',
    phaseDueDateDd: '',
    phaseDueDateCd: '',
    phaseDueDateSsDocs: '',

    designReviewSd: '',
    designReviewDd: '',
    designReviewCd: '',
    designReviewSsDocs: '',

    buildingCodeReviewSd: '',
    buildingCodeReviewDd: '',
    buildingCodeReviewCd: '',
    buildingCodeReviewSsDocs: '',

    qaReviewsSd: '',
    qaReviewsDd: '',
    qaReviewsCd: '',
    qaReviewsSsDocs: '',

    qcPeerReviewSd: '',
    qcPeerReviewDd: '',
    qcPeerReviewCd: '',
    qcPeerReviewSsDocs: '',

    currentPhase: '',
    areasUnderDevelopment: '',

    clientValuesAndGoals: '',
    clientMeasuresForSuccess: [],
    clientMeasuresForSuccessOther: '',
    examineOurProcesses: '',

    reviewedBy: '',
    innovativeProcesses: '',
    innovationDetails: '',
    innovativeProducts: '',

    qaRevitPrompts: '',

    projectFolderLink: '',
    accLink: '',
    centerlineLink: '',

    projectFilePathLinks: {},
  };
};

/**
 * Design Team and Consultant Firms became multi-select user dropdowns; a form saved before that
 * holds one free-text string in each. Read as a one-entry list so the stored name still shows,
 * and is kept, rather than being dropped the next time the form is saved.
 */
const readStoredForm = (data: ProjectQualityControlFormData): FormValues => {
  const toList = (value: string[] | string | undefined) => {
    if (Array.isArray(value)) return value;
    return value ? [value] : [];
  };

  return {
    ...data,
    designTeam: toList(data.designTeam as string[] | string | undefined),
    consultantFirms: toList(data.consultantFirms as string[] | string | undefined),
    // The generated client types this map as `object`, the map having no named type on the wire.
    projectFilePathLinks:
      (data.projectFilePathLinks as Record<string, ProjectFilePathLink> | undefined) ?? {},
  };
};

interface Props {
  formId?: string | null;
  canEdit: boolean;
  isEditing: boolean;
  publicData?: ProjectQualityControlFormData | null;
}

export default function ProjectQualityControlForm(props: Props) {
  const { canEdit, isEditing, formId, publicData } = props;
  const classes = useStyles();

  const dispatch = useDispatch();
  const project = useSelector(getProjectState);
  const projectUsers = useSelector(getProjectUsersState);

  const isMobile = useMediaQuery('(max-width: 800px)');

  const qualityControlForm = useMemo(
    () => project?.projectQualityControlForms?.find((x) => x.id === formId),
    [project, formId],
  );

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    watch,
    formState: { isSubmitting, dirtyFields },
  } = useForm({
    disabled: !canEdit,
    resolver: zodResolver(schema),
    defaultValues: !qualityControlForm && !publicData ? getDefaultValues() : undefined,
  });

  const clientMeasuresForSuccess = watch('clientMeasuresForSuccess');

  // Watched so a stored name that is not among the project's users is still offered as an option
  // by the dropdown rather than silently dropped when the form is saved again.
  const architectOfRecord = watch('architectOfRecord');
  const projectManager = watch('projectManager');
  const designTeam = watch('designTeam');
  const consultantFirms = watch('consultantFirms');
  const qcPeerReviewer1 = watch('qcPeerReviewer1');
  const qcPeerReviewer2 = watch('qcPeerReviewer2');

  const fillFormFieldsFromProject = () => {
    if (!project) return;

    if (project.architectProjectNumber) {
      setValue('projectNumber', project.architectProjectNumber);
    }
    setValue('projectName', project.name);
    if (project.description) {
      setValue('projectDescription', project.description);
    }
    // Whatever of the project's location is set: street, city and state are each optional, and
    // formatProjectLocation leaves out the parts that are missing rather than printing gaps.
    const projectAddress = formatProjectLocation(project.address);
    if (projectAddress) {
      setValue('projectAddress', projectAddress);
    }
  };

  useEffect(() => {
    if (publicData) {
      reset(readStoredForm(publicData));
    } else if (qualityControlForm) {
      if (qualityControlForm?.data) {
        reset(readStoredForm(qualityControlForm.data));
      }
    } else {
      fillFormFieldsFromProject();
    }
  }, [qualityControlForm, publicData]);

  useEffect(() => {
    if (!_.isEmpty(dirtyFields)) {
      dispatch(blockNavigation());
    } else {
      dispatch(allowNavigation());
    }
  }, [dirtyFields]);

  const onSubmit = handleSubmit(
    async (data) => {
      await submit(data);

      reset(undefined, { keepValues: true });

      dispatch(allowNavigation());

      dispatch(
        addSnackbar({ id: Date.now(), message: 'Form saved successfully', severity: 'success' }),
      );
    },
    (errors) => {
      console.log('errors', errors);
    },
  );

  const submit = async (data: FormValues) => {
    if (!project) return;

    if (!formId) {
      await insertProjectQualityControlForm(project.id, data);
    } else {
      await editProjectQualityControlFormById(project.id, formId, data);
    }

    window.scrollTo({ behavior: 'smooth', top: 0, left: 0 });
    dispatch(reloadProject());
  };

  return (
    <form noValidate onSubmit={onSubmit} style={{ paddingBottom: isEditing ? 0 : 16 }}>
      <FormSection>
        <FormFieldLabel>QC Date:</FormFieldLabel>
        <FormDatePicker name="qcDate" control={control} className={classes.printShort} />
      </FormSection>

      <Typography className={classes.subtitle}>
        To be completed at each phase (TYP. SD/DD/CD Phases; Addenda; ASIs; Early Release Package;
        etc.). Provide adequate time for review and red-line pickup before the issue dates.
      </Typography>

      <FormSection title="Details">
        <FormFieldLabel>Project Number:</FormFieldLabel>
        <FormTextField name="projectNumber" control={control} />

        <FormFieldLabel>Project Name:</FormFieldLabel>
        <FormTextField name="projectName" control={control} />

        <FormFieldLabel>Project Address:</FormFieldLabel>
        <FormTextField name="projectAddress" control={control} />

        <FormFieldLabel>Jurisdiction:</FormFieldLabel>
        <FormTextField name="jurisdiction" control={control} />

        <FormFieldLabel>Project Description:</FormFieldLabel>
        <FormTextField name="projectDescription" control={control} large />

        <FormFieldLabel>Architect of Record:</FormFieldLabel>
        <FormUserSelect
          name="architectOfRecord"
          control={control}
          projectUsers={projectUsers}
          value={architectOfRecord}
          disabled={!canEdit}
        />

        <FormFieldLabel>Project Manager:</FormFieldLabel>
        <FormUserSelect
          name="projectManager"
          control={control}
          projectUsers={projectUsers}
          value={projectManager}
          disabled={!canEdit}
        />

        <FormFieldLabel>Design Team:</FormFieldLabel>
        <FormUserSelect
          name="designTeam"
          control={control}
          projectUsers={projectUsers}
          value={designTeam}
          multiple
          disabled={!canEdit}
        />

        <FormFieldLabel>Consultant Firms:</FormFieldLabel>
        <FormUserSelect
          name="consultantFirms"
          control={control}
          projectUsers={projectUsers}
          value={consultantFirms}
          multiple
          disabled={!canEdit}
        />

        <FormFieldLabel>QC Peer Reviewer 1:</FormFieldLabel>
        <FormUserSelect
          name="qcPeerReviewer1"
          control={control}
          projectUsers={projectUsers}
          value={qcPeerReviewer1}
          disabled={!canEdit}
        />

        <FormFieldLabel>QC Peer Reviewer 2:</FormFieldLabel>
        <FormUserSelect
          name="qcPeerReviewer2"
          control={control}
          projectUsers={projectUsers}
          value={qcPeerReviewer2}
          disabled={!canEdit}
        />
      </FormSection>

      <FormSection title="Project File Paths" noGrid>
        {!isMobile ? (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: QUALITY_CONTROL_PHASE_GRID_COLUMNS,
              columnGap: '12px',
              marginBottom: 8,
              fontWeight: 600,
            }}
          >
            <div />
            {QUALITY_CONTROL_PHASE_COLUMNS.map((column) => (
              <Typography key={column.key} style={{ fontWeight: 600 }}>
                {column.label}
              </Typography>
            ))}
          </div>
        ) : null}

        {QUALITY_CONTROL_PHASE_ROWS.map((row) => (
          <ProjectQualityControlPhaseRow
            key={row.key}
            control={control}
            rowKey={row.key}
            label={row.label}
            isDate={row.isDate}
          />
        ))}
      </FormSection>

      <FormSection title="Design Team Report" className={classes.printBreakBefore}>
        <FormFieldLabel>Current Phase:</FormFieldLabel>
        <FormTextField name="currentPhase" control={control} />

        <FormFieldLabel>Areas Under Development:</FormFieldLabel>
        <FormTextField name="areasUnderDevelopment" control={control} />
      </FormSection>

      <FormSection
        title="Lean Principles"
        formGridProps={{ gridTemplateColumns: 'repeat(3, 1fr)' }}
        style={{ alignItems: 'start' }}
      >
        <FormFieldLabel>Customer Focused Quality &mdash; Client Values &amp; Goals:</FormFieldLabel>
        <FormFieldLabel>Waste Elimination &mdash; Client Measures for Success:</FormFieldLabel>
        <FormFieldLabel>Continuous Improvement &mdash; Examine Our Processes:</FormFieldLabel>

        <FormTextField name="clientValuesAndGoals" control={control} large />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignSelf: 'stretch' }}>
          <FormSelect
            name="clientMeasuresForSuccess"
            control={control}
            options={WASTE_ELIMINATION_OPTIONS}
            multiple
            className={classes.fillCell}
            formControlProps={{ style: { flexGrow: 1 } }}
          />
          {clientMeasuresForSuccess?.includes('other') ? (
            <FormTextField
              name="clientMeasuresForSuccessOther"
              control={control}
              placeholder="Please specify"
            />
          ) : null}
        </div>
        <FormTextField name="examineOurProcesses" control={control} large />
      </FormSection>

      <FormSection title="Team Shout-Outs">
        <FormFieldLabel>Reviewed By:</FormFieldLabel>
        <FormTextField name="reviewedBy" control={control} />

        <FormFieldLabel>Innovative Processes:</FormFieldLabel>
        <FormTextField name="innovativeProcesses" control={control} />

        <FormFieldLabel>Innovation Details / Documentation:</FormFieldLabel>
        <FormTextField name="innovationDetails" control={control} />

        <FormFieldLabel>Innovative Products:</FormFieldLabel>
        <FormTextField name="innovativeProducts" control={control} />
      </FormSection>

      <FormSection title="BIM Template">
        <FormFieldLabel>QA Revit Prompts for Consideration for Revit Template:</FormFieldLabel>
        <FormTextField name="qaRevitPrompts" control={control} />
      </FormSection>

      <FormSection title="Reference/Links" style={{ pageBreakInside: 'avoid' }}>
        <FormFieldLabel>Project Folder:</FormFieldLabel>
        <FormTextField name="projectFolderLink" control={control} />

        <FormFieldLabel>ACC:</FormFieldLabel>
        <FormTextField name="accLink" control={control} />

        <FormFieldLabel>Centerline:</FormFieldLabel>
        <FormTextField name="centerlineLink" control={control} />
      </FormSection>

      {isEditing ? (
        <div
          className={classes.hideOnPrint}
          style={{
            position: isMobile ? 'static' : 'sticky',
            bottom: 0,
            width: 'calc(100% + 32px)',
            padding: 16,
            marginLeft: -16,
            backgroundColor: '#FFF',
            borderTop: '1px solid #CCC',
            zIndex: 100,
          }}
        >
          {!isSubmitting ? (
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={!canEdit}
              startIcon={<Save />}
            >
              Save Changes
            </Button>
          ) : (
            <CircularLoader style={{ justifyContent: 'start', paddingLeft: 32 }} />
          )}
        </div>
      ) : null}
    </form>
  );
}
